import React, { useState } from 'react';
import { Check, X, Star, Shield, Users, Clock, Heart, Menu, ArrowLeft } from 'lucide-react';
import { NavLink } from 'react-router-dom';

const PricingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'

  const plans = [
    {
      name: "Basic",
      price: { monthly: 0, yearly: 0 },
      popular: false,
      description: "Perfect for getting started with your digital legacy",
      features: [
        "100MB Total Storage",
        "1 Recipient User", 
        "100MB Upload Limit",
        "Basic Security",
        "Email Support"
      ],
      limitations: [
        "Limited storage space",
        "Single recipient only",
        "Basic features only"
      ]
    },
    {
      name: "Gold",
      price: { monthly: 10, yearly: 100 },
      popular: true,
      description: "Most popular choice for families",
      features: [
        "150GB Total Storage",
        "Up to 60 Recipients",
        "Unlimited Photo/Video Storage",
        "Advanced Security & Encryption",
        "Priority Support",
        "Legacy Distribution",
        "Backup Scheduler",
        "Mobile & Desktop Access"
      ],
      limitations: []
    },
    {
      name: "Platinum",
      price: { monthly: 20, yearly: 175 },
      popular: false,
      description: "Ultimate protection for large families",
      features: [
        "Unlimited Storage",
        "Up to 120 Recipients",
        "2TB Upload Limit",
        "Enterprise-Grade Security",
        "24/7 Premium Support",
        "Advanced Legacy Features",
        "Custom Sharing Options",
        "White-Glove Setup"
      ],
      limitations: []
    }
  ];

  const comparisonFeatures = [
    {
      feature: "30-Day Free Account",
      basic: "Limited access",
      gold: "2TB with premium access",
      platinum: "Unlimited access"
    },
    {
      feature: "Total Storage",
      basic: "100MB",
      gold: "Up to 150GB",
      platinum: "Unlimited"
    },
    {
      feature: "Price Plan",
      basic: "Free",
      gold: "Flexible and affordable",
      platinum: "Premium features"
    },
    {
      feature: "Auto Memory Backup",
      basic: false,
      gold: true,
      platinum: true
    },
    {
      feature: "Backup Scheduler",
      basic: false,
      gold: "Schedule regular backups",
      platinum: "Advanced scheduling"
    },
    {
      feature: "Legacy Distribution",
      basic: false,
      gold: "Automatically assigns files to heirs",
      platinum: "Advanced distribution"
    },
    {
      feature: "Fast Upload Speeds",
      basic: "Basic speed",
      gold: "Optimized for fast upload",
      platinum: "Premium speeds"
    },
    {
      feature: "High Daily Upload Limit",
      basic: "Limited",
      gold: "Up to 100GB/day",
      platinum: "Unlimited daily uploads"
    },
    {
      feature: "Secure Digital Storage",
      basic: "Basic security",
      gold: "Advanced encryption",
      platinum: "Enterprise security"
    },
    {
      feature: "Customer Support",
      basic: "Email only",
      gold: "Dedicated legacy support",
      platinum: "24/7 premium support"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50 font-sans">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-lg border-b border-orange-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
          <div className="flex items-center space-x-3">
              <NavLink to={'/'} >
                <img src="images/log.svg" />
              </NavLink>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-10">
              {/* <a href="/" className=" font-semibold text-lg border-b-2 border-orange-500 text-orange-500 pb-1">Home</a> */}
              <a href="/" className="text-gray-700 hover:text-orange-500 transition-colors text-lg font-medium">Home</a>
              <a href="features" className="text-gray-700 hover:text-orange-500 transition-colors text-lg font-medium">Features</a>
              <a href="/pricing" className=" font-semibold text-lg border-b-2 border-orange-500 text-orange-500 pb-1">Package</a>
              <a href="contact-us" className="text-gray-700 hover:text-orange-500 transition-colors text-lg font-medium">Contact</a>
              <a href='sign-in' className="text-orange-500 hover:text-orange-600 transition-colors text-lg font-medium">Sign in</a>
              <a href='register' className="bg-orange-500 text-white px-8 py-3 rounded-xl hover:bg-orange-600 transition-all duration-300 font-semibold text-lg shadow-lg hover:shadow-xl">
                Sign up
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white/95 backdrop-blur-lg border-t border-orange-100 shadow-lg">
            <div className="px-6 py-6 space-y-4">
              <a href="/" className="block py-3 text-grey-700 font-semibold text-xl">Home</a>
              <a href="features" className="block py-3 text-gray-700 text-xl">Features</a>
              <a href="pricing" className="block py-3 text-orange-500 text-xl">Package</a>
              <a href="contact-us" className="block py-3 text-gray-700 text-xl">Contact</a>
              <div className="flex flex-col space-y-3 pt-4">
                <a href='/sign-in' className="text-orange-500 text-xl font-medium text-left">Sign in</a>
                <a href='/register' className="bg-orange-500 text-white px-6 py-3 rounded-xl text-lg font-semibold w-full">Sign up</a>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Find the right
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
              bEternal package plan
            </span>
            for you
          </h1>
          <p className="text-lg md:text-xl text-gray-600 mb-12 leading-relaxed">
            Choose the perfect plan to preserve your legacy and protect what matters most to your family
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center mb-16">
            <div className="bg-white rounded-full p-1 shadow-lg border border-gray-200">
              <div className="flex">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 ${
                    billingCycle === 'monthly'
                      ? 'bg-orange-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-orange-500'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('yearly')}
                  className={`px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 ${
                    billingCycle === 'yearly'
                      ? 'bg-orange-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-orange-500'
                  }`}
                >
                  Yearly (Save 20%)
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {plans.map((plan:any, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 ${
                  plan.popular ? 'ring-4 ring-orange-500 ring-opacity-50 scale-105' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      RECOMMENDED
                    </div>
                  </div>
                )}

                <div className="p-8 lg:p-10">
                  {/* Plan Header */}
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 text-sm mb-6">{plan.description}</p>
                    
                    <div className="mb-6">
                      <div className="flex items-baseline justify-center">
                        <span className="text-4xl font-bold text-gray-900">
                          ${plan.price[billingCycle]}
                        </span>
                        <span className="text-gray-600 ml-2">
                          /{billingCycle === 'yearly' ? 'year' : 'month'}
                        </span>
                      </div>
                      {billingCycle === 'yearly' && plan.price.yearly > 0 && (
                        <p className="text-sm text-green-600 mt-2">
                          Save ${(plan.price.monthly * 12) - plan.price.yearly} per year
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="space-y-4 mb-8">
                    {plan.features.map((feature:any, featureIndex:any) => (
                      <div key={featureIndex} className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                    
                    {plan.limitations.map((limitation:any, limitIndex:any) => (
                      <div key={limitIndex} className="flex items-start space-x-3">
                        <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-500 text-sm">{limitation}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <button className={`w-full py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg ${
                    plan.popular
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:shadow-xl'
                      : 'bg-gray-900 text-white hover:bg-gray-800 hover:shadow-xl'
                  }`}>
                    <a href='/register'> Get Started{plan.name === 'Basic' ? ' For Free' : ''}</a>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Comparison Table */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Discover Why bETERNAL Is The Best Solution
            </h2>
            <p className="text-lg text-gray-600">
              bETERNAL vs Dropbox And Social Media
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-2xl shadow-xl overflow-hidden">
              <thead>
                <tr className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
                  <th className="px-6 py-4 text-left font-semibold">Feature</th>
                  <th className="px-6 py-4 text-center font-semibold">bETERNAL</th>
                  <th className="px-6 py-4 text-center font-semibold">Dropbox</th>
                  <th className="px-6 py-4 text-center font-semibold">Social Media</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {comparisonFeatures.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">{item.feature}</td>
                    <td className="px-6 py-4 text-center">
                      {typeof item.basic === 'boolean' ? (
                        item.gold ? (
                          <div className="flex items-center justify-center">
                            <Check className="w-5 h-5 text-green-500" />
                            <span className="ml-2 text-sm text-green-600">Yes</span>
                          </div>
                        ) : (
                          <X className="w-5 h-5 text-red-400 mx-auto" />
                        )
                      ) : (
                        <span className="text-sm text-green-600 font-medium">{item.gold}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {typeof item.basic === 'boolean' ? (
                        <X className="w-5 h-5 text-red-400 mx-auto" />
                      ) : (
                        <span className="text-sm text-gray-600">Standard features</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <X className="w-5 h-5 text-red-400 mx-auto" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-center mt-12">
            <a href='/register' className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-10 py-4 rounded-xl text-lg font-semibold hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              Get Started For Free Now!
            </a>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center text-white mb-4 mx-auto">
                <Shield className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Enterprise Security</h3>
              <p className="text-gray-600 text-sm">Bank-level encryption protects your memories</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-orange-500 rounded-2xl flex items-center justify-center text-white mb-4 mx-auto">
                <Users className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Family Focused</h3>
              <p className="text-gray-600 text-sm">Designed specifically for family legacy</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-orange-500 rounded-2xl flex items-center justify-center text-white mb-4 mx-auto">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Always Available</h3>
              <p className="text-gray-600 text-sm">24/7 access to your digital legacy</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl flex items-center justify-center text-white mb-4 mx-auto">
                <Heart className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Built with Love</h3>
              <p className="text-gray-600 text-sm">Created by families, for families</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">What happens to my files after I pass away?</h3>
              <p className="text-gray-600 leading-relaxed">
                Your files remain securely stored in our encrypted system. We use an advanced activity-tracking system to detect periods of inactivity, and your chosen heirs will be notified to access the files you've pre-selected to share with them.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Is my data encrypted and secure?</h3>
              <p className="text-gray-600 leading-relaxed">
                Yes, absolutely. We use enterprise-grade encryption, secure storage, and constant monitoring to guard against unauthorized access. Your privacy and data safety are our top priorities.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Can I change my plan later?</h3>
              <p className="text-gray-600 leading-relaxed">
                Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle, and we'll help you migrate your data seamlessly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                  <span className="text-black font-bold text-lg">b</span>
                </div>
                <span className="text-2xl font-bold">ETERNAL</span>
              </div>
              <p className="text-gray-400 text-lg font-light">YOUR LEGACY, YOUR WAY.</p>
            </div>

            <div>
              <h4 className="font-bold text-xl mb-6">Site Link</h4>
              <ul className="space-y-3 text-gray-400">
                <li><a href="/" className="hover:text-white transition-colors text-lg">Home</a></li>
                <li><a href="features" className="hover:text-white transition-colors text-lg">Features</a></li>
                <li><a href="pricing" className="hover:text-white transition-colors text-lg">Packages</a></li>
                <li><a href="contact-us" className="hover:text-white transition-colors text-lg">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-xl mb-6">Legal</h4>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors text-lg">Terms & Conditions</a></li>
                <li><a href="#" className="hover:text-white transition-colors text-lg">Help & Support</a></li>
                <li><a href="#" className="hover:text-white transition-colors text-lg">Privacy Policy</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-xl mb-6">Social Media</h4>
              <div className="flex space-x-4">
                {/* Facebook */}
                <a
                  href="https://www.facebook.com/share/18tWm4dH8V/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-blue-600 rounded-2xl hover:scale-110 transition-transform cursor-pointer flex items-center justify-center"
                  aria-label="Visit our Facebook page"
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>

                {/* LinkedIn */}
                <a
                  href="https://www.linkedin.com/company/beternal/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-blue-500 rounded-2xl hover:scale-110 transition-transform cursor-pointer flex items-center justify-center"
                  aria-label="Visit our LinkedIn page"
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>

                {/* YouTube */}
                <a
                  href="https://www.youtube.com/@beternal4543?si=LY1iOIxF-jzEaoY9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-red-600 rounded-2xl hover:scale-110 transition-transform cursor-pointer flex items-center justify-center"
                  aria-label="Visit our YouTube channel"
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-16 pt-8 text-center text-lg text-gray-400 font-light">
            bETERNAL ® | © 2025 all rights reserved
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;